import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Input } from "@/components/_atoms/input";
import { Switch } from "@/components/_atoms/switch";
import { ColorPickerHex } from "@/components/_molecules/color-picker/color-picker-hex";

import { UpdateLabelGroupAction } from "./label-group-action-button/actions/update-label-group-action";

export interface LabelGroupEditFormProps {
  labelGroup: {
    id: string;
    title: string;
    color: string;
    allowMultiSelect: boolean;
  };
  className?: string;
  onSuccess?: () => void;
  onCanceled?: () => void;
  onAllowMultiSelectChange?: (value: boolean) => void;
}

export const LabelGroupEditForm: FC<LabelGroupEditFormProps> = ({ labelGroup, onSuccess, onCanceled, onAllowMultiSelectChange }) => {
  const { t } = useTranslation("common");
  const ref = useRef<HTMLInputElement>(null);
  const [labelGroupName, setLabelGroupName] = useState(labelGroup.title);
  const [labelGroupColor, setLabelGroupColor] = useState(labelGroup.color);
  const [allowMultiSelect, setAllowMultiSelect] = useState(labelGroup.allowMultiSelect);

  const updatedLabelGroup = useMemo(() => {
    return {
      id: labelGroup.id,
      title: labelGroupName,
      color: labelGroupColor,
      allowMultiSelect: allowMultiSelect,
    };
  }, [labelGroup, labelGroupName, labelGroupColor, allowMultiSelect]);

  function onUpdateCompleted() {
    if (onSuccess) {
      onSuccess();
    }
  }

  function onUpdateError() {
    toast.error(t("errors.title"), { description: t("errors.generic") });
  }

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div className=" p-1">
      <div className="flex flex-row items-center justify-between gap-2">
        <ColorPickerHex value={labelGroupColor} onChange={(color) => setLabelGroupColor(color)} />
        <div className="grow">
          <Input
            ref={ref}
            autoFocus
            value={labelGroupName}
            onChange={(e) => setLabelGroupName(e.currentTarget.value)}
            placeholder={t("label.groupNamePlaceholder")}
            className="bg-background focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus"
          />
        </div>
        <div>
          <Button variant="outline" size="sm" className="cursor-pointer" onClick={onCanceled}>
            {t("actions.cancel")}
          </Button>
        </div>
        <div>
          <UpdateLabelGroupAction
            labelGroup={updatedLabelGroup}
            onSuccess={onUpdateCompleted}
            onError={onUpdateError}
            asChild
          >
            <Button variant="default" size="sm" className="cursor-pointer" disabled={labelGroupName === ""}>
              {t("actions.save")}
            </Button>
          </UpdateLabelGroupAction>
        </div>
      </div>
      <div className="ml-12 mt-2">
        <div className="flex items-center space-x-1 text-sm text-muted-foreground">
          <Switch
          id="label-group-multi-select"
          aria-label={t("label.allowMultiSelect")}
          checked={allowMultiSelect}
          onCheckedChange={(value) => {
            setAllowMultiSelect(value);
            onAllowMultiSelectChange?.(value);
          }}
        />
          <label htmlFor="label-group-multi-select">{t("label.allowMultiSelect")}</label>
        </div>
      </div>
    </div>
  );
};
